export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export type AvatarTone = 'neutral' | 'brand';

const TONES: Record<AvatarTone, string> = {
  // The default everywhere: a grey disc that never competes with the content beside it.
  neutral: 'bg-slate-200 text-slate-600',
  // Reserved for the signed-in identity in the app bar.
  brand: 'bg-brand-500 text-white',
};

const DIMENSIONS: Record<AvatarSize, string> = {
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-11 w-11 text-sm',
  /*
    The header strip's subject. The ramp used to step 44px straight to 96px, which left a
    header profile choosing between an avatar the size of a row icon and one taller than the
    strip it sits in — so the step that was missing is the one a header actually wants.
  */
  xl: 'h-14 w-14 text-lg',
  // The profile SIDEBAR's identity block, where the avatar is centred above everything.
  '2xl': 'h-24 w-24 text-2xl',
};

/**
 * Brand avatar — owns initials, colour, and dimensions for every person surface.
 *
 * Deliberately one colour per tone, never one per person. Hashing a name to a hue reads as a
 * status signal the data does not actually carry, and it strands anyone whose name
 * transliterates oddly.
 */
export function Avatar({
  name,
  email,
  imageUrl,
  size = 'md',
  tone = 'neutral',
  className = '',
}: {
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  size?: AvatarSize;
  tone?: AvatarTone;
  className?: string;
}) {
  const initials = deriveInitials(name, email);

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        className={`shrink-0 rounded-full object-cover ${DIMENSIONS[size]} ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full font-medium ${TONES[tone]} ${DIMENSIONS[size]} ${className}`}
    >
      {initials}
    </span>
  );
}

/**
 * First letter of the first two words of a name; falls back to the email's local part, and
 * finally to a neutral dash rather than rendering an empty circle.
 */
function deriveInitials(name?: string | null, email?: string | null): string {
  const source =
    name?.trim() ||
    email
      ?.split('@')[0]
      ?.replace(/[._-]+/g, ' ')
      .trim();

  if (!source) {
    return '–';
  }

  const words = source.split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return '–';
  }

  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];

  return letters.toUpperCase();
}
